<template>
  <div class="password-change details">
    <div class="title-block my-0">
      <button class="return" @click="$router.back()">
        <v-icon>mdi-keyboard-return</v-icon>
      </button>
      <h1 class="text-h2 text-center mb-16">Changement de mot de passe</h1>
    </div>
    <form @submit.prevent="changePassword" class="mt-n8">
      <v-container fluid style="max-width: 1440px">
        <v-row
          class="justify-center flex-column align-center"
          style="transform: scale(0.85)"
        >
          <div class="d-flex flex-wrap">
            <v-icon color="warning" icon="$warning" size="large">
            </v-icon><p>Ne doit pas contenir votre nom ou prénom.</p>
          </div>
          <v-col cols="6" md="5" class="position-relative">
            <!-- OLD PASSWORD -->
            <v-col cols="12" class="accent px-7 rounded-lg mb-10 elevation-1">
              <v-text-field
                v-model="oldPassword"
                :append-icon="showOld ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showOld ? 'text' : 'password'"
                label="Ancien mot de passe"
                @click:append="showOld = !showOld"
                variant="underlined"
                required
                color="primary"
              ></v-text-field>
            </v-col>

            <!-- NEW PASSWORD -->
            <v-col cols="12" class="accent px-7 rounded-lg mb-10 elevation-1">
              <v-text-field
                v-model="newPassword"
                :append-icon="showNew ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showNew ? 'text' : 'password'"
                label="Nouveau mot de passe"
                hint="Minimum 8 caractères, une majuscule, un chiffre et un caractère spécial"
                @click:append="showNew = !showNew"
                @input="validatePassword"
                variant="underlined"
                required
                :color="isPasswordValid ? 'success' : 'error'"
              ></v-text-field>
            </v-col>

            <!-- CONFIRM PASSWORD -->
            <v-col cols="12" class="accent px-7 rounded-lg elevation-1">
              <v-text-field
                v-model="confirmPassword"
                :append-icon="showConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showConfirm ? 'text' : 'password'"
                label="Confirmer le mot de passe"
                @click:append="showConfirm = !showConfirm"
                variant="underlined"
                required
                :color="confirmPassword === newPassword ? 'success' : 'error'"
              ></v-text-field>
            </v-col>

            <!-- SUBMIT -->
            <v-col class="px-7 mt-7 d-flex justify-center">
              <v-btn
                type="submit"
                :disabled="!isFormValid"
                color="primary"
                class="d-flex pa-7 px-11 primary rounded-lg"
                :loading="loading"
              >
                Changer le mot de passe
              </v-btn>
            </v-col>

            <!-- Validation Rules -->
            <v-card
              :class="{
                'validation-rules hidden': newPassword.length < 1,
                'validation-rules': newPassword.length > 0,
              }"
              elevation="2"
            >
              <v-list>
                <v-list-item
                  v-for="(rule, index) in passwordRules"
                  :key="index"
                >
                  <template v-slot:prepend>
                    <v-icon :color="rule.valid ? 'success' : 'error'">
                      {{ rule.valid ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                  </template>
                  <v-list-item-title
                    :class="{
                      'text-success': rule.valid,
                      'text-error': !rule.valid,
                    }"
                  >
                    {{ rule.text }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </form>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000">
      {{ snackbarMessage }}
    </v-snackbar>
    <FooterBar></FooterBar>

  </div>
</template>

<script>
import axios from "axios";
import { base_url, user_urls, header } from "@/API/config.js";
import FooterBar from "@/components/Footer.vue";

export default {
  name: "PasswordNew",
  components: {FooterBar},
  data: () => ({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    error: "",
    showOld: false,
    showNew: false,
    showConfirm: false,
    snackbar: false,
    snackbarMessage: "",
    snackbarColor: "success",
    loading: false,
    passwordRules: [
      { text: "Au moins 8 caractères", valid: false },
      { text: "Au moins une majuscule", valid: false },
      { text: "Au moins un chiffre", valid: false },
      { text: "Au moins un caractère spécial", valid: false },
    ],
  }),
  computed: {
    isPasswordValid() {
      return this.passwordRules.every((rule) => rule.valid);
    },
    isFormValid() {
      return (
        this.isPasswordValid &&
        this.newPassword === this.confirmPassword &&
        this.oldPassword !== this.newPassword &&
        this.oldPassword !== ""
      );
    },
  },
  methods: {
    validatePassword() {
      this.passwordRules[0].valid = this.newPassword.length >= 8;
      this.passwordRules[1].valid = /[A-Z]/.test(this.newPassword);
      this.passwordRules[2].valid = /\d/.test(this.newPassword);
      this.passwordRules[3].valid = /[!@#$%^&*(),.?":{}|<>]/.test(
        this.newPassword
      );
    },
    async changePassword() {
      this.loading = true;
      this.error = "";

      if (!this.isFormValid) {
        this.error = "Veuillez vérifier tous les champs.";
        this.snackbarMessage = this.error;
        this.snackbar = true;
        this.snackbarColor = "error";
        this.loading = false;
        return;
      }

      const payload = {
        old_password: this.oldPassword,
        new_password: this.newPassword,
      };

      const url = `${base_url}${user_urls.change_password}`;
      try {
        await axios.post(url, payload, header);
        this.snackbarMessage = "Mot de passe changé avec succès!";
        this.snackbar = true;
        this.snackbarColor = "success";

        setTimeout(() => this.$router.push("/account"), 2000);
      } catch (error) {
        if (error.response && error.response.data) {
          if (error.response.data.error) {
            this.error = error.response.data.error;
          } else {
            this.error = Object.values(error.response.data).flat().join(" ");
          }
        } else {
          this.error =
            "Une erreur est survenue lors du changement de mot de passe.";
        }

        this.snackbarMessage = this.error;
        this.snackbar = true;
        this.snackbarColor = "error";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    document.title = "Changement Mot de Passe";
  },
};
</script>

<style lang="scss" scoped>
.password-change {
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.position-relative {
  position: relative;
}

.validation-rules {
  position: absolute;
  top: 10px;
  right: -340px;
  width: 330px;
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  &.hidden {
    opacity: 0;
  }
}

@media (max-width: 1200px) {
  .validation-rules {
    position: static;
    margin-top: 20px;
    width: 100%;
  }
}
</style>
